import React, { useEffect, useState } from 'react';

import { timingBridgeAPI } from '../../../lib/api';

/**
 * Screen 3 — Diagnostics.
 *
 * The question this answers: **is the number on screen wrong because the
 * timing site is not moving, or because the bridge is reading it wrong?** The
 * raw responses answer the first half; the payload viewer on the Live screen
 * answers the second.
 */
export default function DiagnosticsPanel({ summary, showId }) {
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [log, setLog] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!showId) return undefined;
    let cancelled = false;
    const load = () => timingBridgeAPI.upstream(showId, { limit: 20, ...(errorsOnly ? { kind: 'poll-error' } : {}) })
      .then(res => { if (!cancelled) setLog(res.data); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 3000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [showId, errorsOnly]);

  const health = summary?.health;
  const warnings = health?.warnings || [];
  const source = health?.upstream?.source;

  return (
    <div className="space-y-5">
      <section>
        <h2 className="text-sm font-semibold text-white mb-2">Source</h2>
        <div className="px-4 py-3 bg-[#111111] border border-[#2a2a2a] rounded text-xs space-y-1">
          <div className="text-gray-300">{summary?.provider}: <a href={summary?.url} target="_blank" rel="noreferrer" className="text-blue-500 hover:text-blue-400">{summary?.url}</a></div>
          {source && <pre className="text-[10px] text-gray-500 whitespace-pre-wrap">{JSON.stringify(source, null, 1)}</pre>}
          {health?.upstream?.lastError && (
            <div className="text-red-400">Last error{health.upstream.lastErrorCode ? ` (${health.upstream.lastErrorCode})` : ''}: {health.upstream.lastError}</div>
          )}
        </div>
      </section>

      {warnings.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-white mb-2">Warnings</h2>
          <div className="space-y-1">
            {warnings.slice().reverse().map((w, i) => (
              <div key={i} className="px-3 py-1.5 bg-amber-900/20 border border-amber-800 rounded text-xs text-amber-300">
                <span className="text-amber-500/70 mr-2">{w.type}</span>{w.message}
                {w.count > 1 && <span className="ml-2 text-amber-500/60">×{w.count}</span>}
                <span className="ml-2 text-amber-500/50">{new Date(w.lastSeenAt).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-white">Raw responses</h2>
          <label className="flex items-center gap-2 text-xs text-gray-400">
            <input type="checkbox" checked={errorsOnly} onChange={e => setErrorsOnly(e.target.checked)} />
            Errors only
          </label>
        </div>
        <p className="text-[10px] text-gray-600 mb-2">
          The last responses from the timing site, newest first, held in memory only. {log?.capture ? `${log.capture.entries} since start.` : ''}
        </p>
        <div className="space-y-1">
          {(log?.entries || []).map(entry => (
            <div key={entry.seq} className="bg-[#111111] border border-[#2a2a2a] rounded text-xs">
              <button onClick={() => setExpanded(e => (e === entry.seq ? null : entry.seq))} className="w-full flex items-center gap-3 px-3 py-1.5 text-left">
                <span className="text-gray-600 tabular-nums w-20">{new Date(entry.ts).toLocaleTimeString()}</span>
                <span className={entry.error ? 'text-red-400' : 'text-gray-300'}>{entry.kind}</span>
                <code className="text-gray-500 truncate flex-1">{entry.path}</code>
                <span className="text-gray-600 tabular-nums">{entry.status ?? '-'}</span>
                <span className="text-gray-600 tabular-nums w-14 text-right">{entry.ms != null ? `${entry.ms} ms` : ''}</span>
                <code className="text-gray-700 w-16 truncate">{entry.sha}</code>
              </button>
              {entry.error && <div className="px-3 pb-1.5 text-red-400">{entry.error}</div>}
              {expanded === entry.seq && (
                <pre className="px-3 pb-2 text-[10px] text-gray-400 overflow-auto max-h-80">{JSON.stringify(entry.data, null, 2)}</pre>
              )}
            </div>
          ))}
          {!log?.entries?.length && <p className="text-xs text-gray-600">Nothing yet.</p>}
        </div>
      </section>
    </div>
  );
}
